/**
 * 営業データ連携（新製品シミュレーション画面の「営業データ連携」タブ）
 * 共通の型・定数定義。
 *
 * 本フェーズはフロントエンドのみの実装であり、検索結果・AI試算結果は
 * design_handoff (SalesDataLinkage.dc.html) と同様のモックデータ・モックロジックを踏襲する。
 */

/** サブタブ（ステップ）のID */
export type SalesDataLinkageSubView =
  | "industry"
  | "product"
  | "customer"
  | "advantage"
  | "confirm"
  | "ai";

/** データ軸（業界/製品/客先）のキー */
export type AxisKey = "industry" | "product" | "customer";

/** 優位性評価の3項目のキー */
export type AdvantageKey = "superiority" | "contract" | "competition";

/** 表示通貨コード */
export type CurrencyCode = "JPY" | "USD";

/** 検索結果グラフの表示指標（金額／単価／数量） */
export type ResultMetric = "amount" | "unitPrice" | "quantity";

/** 5段階評価の1値 */
export type RatingValue = 1 | 2 | 3 | 4 | 5;

export interface RatingScaleItem {
  sym: string;
  labelKey: string;
  val: RatingValue;
  color: string;
}

/** 5段階評価（◎/〇/△/▲/✖） */
export const RATING_SCALE: RatingScaleItem[] = [
  { sym: "◎", labelKey: "veryGood", val: 5, color: "#16a34a" },
  { sym: "〇", labelKey: "good", val: 4, color: "#2f6bd4" },
  { sym: "△", labelKey: "neutral", val: 3, color: "#d97706" },
  { sym: "▲", labelKey: "concern", val: 2, color: "#ea580c" },
  { sym: "✖", labelKey: "bad", val: 1, color: "#dc2626" },
];

/** データ軸メタ情報（軸ごとに計画達成率などのモック値が変わる） */
export interface AxisMeta {
  key: AxisKey;
  /** 軸名の翻訳キー（tab.<key> を参照） */
  labelKey: string;
  /** ストレス係数試算での重み（通貨換算のモック係数としても流用） */
  factor: number;
  /** 通期計画達成率（モック値・％） */
  achieve: number;
  /** 評価観点の一言（AI分析のインプット表示等で使用）の翻訳キー（axisMeta.<key>.aspect を参照） */
  aspectKey: string;
  /** 評価時のガイド文言の翻訳キー（axisMeta.<key>.guide を参照） */
  guideKey: string;
}

export const AXIS_ORDER: AxisKey[] = ["industry", "product", "customer"];

export const AXIS_META: Record<AxisKey, AxisMeta> = {
  industry: {
    key: "industry",
    labelKey: "industry",
    factor: 1.1,
    achieve: 105.1,
    aspectKey: "industry",
    guideKey: "industry",
  },
  product: {
    key: "product",
    labelKey: "product",
    factor: 1.0,
    achieve: 102.2,
    aspectKey: "product",
    guideKey: "product",
  },
  customer: {
    key: "customer",
    labelKey: "customer",
    factor: 0.88,
    achieve: 98.4,
    aspectKey: "customer",
    guideKey: "customer",
  },
};

/** ステッパー（サブタブ）定義 */
export interface SubTabDef {
  id: SalesDataLinkageSubView;
  labelKey: string;
}

export const SUB_TAB_DEFS: SubTabDef[] = [
  { id: "industry", labelKey: "industry" },
  { id: "product", labelKey: "product" },
  { id: "customer", labelKey: "customer" },
  { id: "advantage", labelKey: "advantage" },
  { id: "confirm", labelKey: "confirm" },
  { id: "ai", labelKey: "ai" },
];

/** 検索条件フィルタ（コード＋選択肢） */
export interface FilterDef {
  code: string;
  labelKey: string;
  options: string[];
}

export const FILTER_DEFS: FilterDef[] = [
  { code: "MCID", labelKey: "machineId", options: ["全て", "T-001", "T-002", "T-003"] },
  { code: "CPCD", labelKey: "companyCode", options: ["全て", "0010", "0020", "0030", "0040"] },
  { code: "PGCD", labelKey: "productOrGoodsType", options: ["全て", "製品", "商品"] },
  { code: "SOCD", labelKey: "sourceOrShipperType", options: ["全て", "自社", "委託", "他社"] },
  { code: "PROD", labelKey: "salesItem", options: ["全て", "PRD-4410", "PRD-2205", "PRD-3380"] },
  { code: "PART", labelKey: "partNo", options: ["全て", "MB-2200-A", "SR-1180-K", "HD-9040-X"] },
  { code: "BTCD", labelKey: "billTo", options: ["全て", "BT-204815", "BT-118032", "BT-330671"] },
  { code: "STCD", labelKey: "shipTo", options: ["全て", "ST-204815-01", "ST-118032-09"] },
  { code: "APCD", labelKey: "usage", options: ["全て", "車載", "産業機器", "民生機器"] },
  { code: "GRAD", labelKey: "grade", options: ["全て", "A", "B", "C"] },
  { code: "SPEC", labelKey: "spec", options: ["全て", "標準", "高精度", "特注"] },
  { code: "3SPC", labelKey: "spec3s", options: ["全て", "S1", "S2", "S3"] },
  { code: "RTNR", labelKey: "retainer", options: ["全て", "RT-01", "RT-02"] },
  { code: "CTSR", labelKey: "customerSeries", options: ["全て", "SER-A", "SER-B"] },
  { code: "CTMD", labelKey: "customerModel", options: ["全て", "MDL-100", "MDL-200"] },
  { code: "CTPT", labelKey: "customerPartNo", options: ["全て", "CP-9001", "CP-9002"] },
  { code: "GLAC", labelKey: "globalAccount", options: ["全て", "GL-4000", "GL-4100"] },
];

/** 優位性評価：ラジオ選択肢グループ */
export interface AdvantageRadioOptionDef {
  v: string;
  labelKey: string;
}
export interface AdvantageRadioGroupDef {
  key: string;
  labelKey: string;
  options: AdvantageRadioOptionDef[];
}
export interface AdvantageItemDef {
  key: AdvantageKey;
  labelKey: string;
  descKey: string;
  radios: AdvantageRadioGroupDef[];
}

export const ADVANTAGE_ITEM_DEFS: AdvantageItemDef[] = [
  {
    key: "superiority",
    labelKey: "superiority",
    descKey: "superiorityDesc",
    radios: [
      { key: "ip", labelKey: "ip", options: [{ v: "yes", labelKey: "yes" }, { v: "no", labelKey: "no" }] },
      { key: "codev", labelKey: "codev", options: [{ v: "yes", labelKey: "yes" }, { v: "no", labelKey: "no" }] },
    ],
  },
  {
    key: "contract",
    labelKey: "contract",
    descKey: "contractDesc",
    radios: [
      { key: "track", labelKey: "track", options: [{ v: "yes", labelKey: "yes" }, { v: "no", labelKey: "no" }] },
    ],
  },
  {
    key: "competition",
    labelKey: "competition",
    descKey: "competitionDesc",
    radios: [
      { key: "position", labelKey: "position", options: [{ v: "1st", labelKey: "firstPlace" }, { v: "2nd", labelKey: "secondPlaceOrLower" }] },
    ],
  },
];

/** 表示通貨定義 */
export interface CurrencyDef {
  code: CurrencyCode;
  unit: string;
  symbol: string;
  rate: number;
  dp: number;
}

export const CURRENCY_DEFS: CurrencyDef[] = [
  { code: "JPY", unit: "千JPY", symbol: "¥", rate: 1, dp: 0 },
  { code: "USD", unit: "千USD", symbol: "$", rate: 1 / 152, dp: 1 },
];

/** 月次グラフの元データ（JPY・モック値・金額） */
export const ACTUAL_JPY: (number | null)[] = [312, 298, 356, 349, 371, 410, null, null, null, null, null, null];
export const PLAN_JPY: (number | null)[] = [300, 310, 330, 350, 370, 400, 405, 415, 425, 440, 455, 470];
export const EXPECT_CONFIRMED_JPY: (number | null)[] = [null, null, null, null, null, null, 300, 310, 320, 300, 315, 345];
export const EXPECT_UNCONFIRMED_JPY: (number | null)[] = [null, null, null, null, null, null, 110, 95, 130, 140, 150, 160];

/** 月次グラフの元データ（モック値・単価。JPY／個） */
export const ACTUAL_UPR: (number | null)[] = [12.7, 12.5, 14.2, 14.1, 14.5, 15.3, null, null, null, null, null, null];
export const PLAN_UPR: (number | null)[] = [12.5, 12.7, 13.1, 13.6, 14.0, 14.8, 14.9, 15.1, 15.3, 15.6, 15.9, 16.2];
export const EXPECT_CONFIRMED_UPR: (number | null)[] = [null, null, null, null, null, null, 12.8, 13.0, 13.2, 12.9, 13.3, 13.9];
export const EXPECT_UNCONFIRMED_UPR: (number | null)[] = [null, null, null, null, null, null, 13.4, 13.4, 14.0, 14.3, 14.7, 14.8];

/** 月次グラフの元データ（モック値・数量。個。通貨換算は行わない） */
export const ACTUAL_QTY: (number | null)[] = [24500, 23800, 25100, 24700, 25600, 26800, null, null, null, null, null, null];
export const PLAN_QTY: (number | null)[] = [24000, 24500, 25200, 25800, 26400, 27000, 27200, 27500, 27800, 28200, 28600, 29000];
export const EXPECT_CONFIRMED_QTY: (number | null)[] = [null, null, null, null, null, null, 23500, 23800, 24200, 23200, 23600, 24800];
export const EXPECT_UNCONFIRMED_QTY: (number | null)[] = [null, null, null, null, null, null, 8200, 7100, 9300, 9800, 10200, 10800];

/** 通期着地見込（JPY・モック値。表示通貨・分析軸係数を掛けて利用） */
export const BASE_LANDING_JPY = 4771;

/** KPI算出のもととなるモック値（JPY） */
export const KPI_MOCK_JPY = {
  ytdActual: 2096,
  landing: BASE_LANDING_JPY,
  prevLanding: 4380,
  confirmedExpect: 1890,
  unconfirmedExpect: 785,
};

/** KPIカードの並び順・アクセント色（design_handoff準拠：5枚） */
export interface KpiDef {
  key: "ytdActual" | "landing" | "achieveRate" | "yoy" | "confirmedRate";
  labelKey: string;
  noteKey: string;
  accent: string;
}
export const KPI_DEFS: KpiDef[] = [
  { key: "ytdActual", labelKey: "ytdActual", noteKey: "ytdActualNote", accent: "#2f6bd4" },
  { key: "landing", labelKey: "landing", noteKey: "landingNote", accent: "#7c5cff" },
  { key: "achieveRate", labelKey: "achieveRate", noteKey: "achieveRateNote", accent: "#16a34a" },
  { key: "yoy", labelKey: "yoy", noteKey: "yoyNote", accent: "#2f6bd4" },
  { key: "confirmedRate", labelKey: "confirmedRate", noteKey: "confirmedRateNote", accent: "#a78bfa" },
];

/** 3グラフ（実績/計画/見込）の定義（design_handoff準拠） */
export interface ChartDef {
  id: "actual" | "plan" | "expect";
  titleKey: string;
  sourceKey: string;
  tagColor: string;
}
export const CHART_DEFS: ChartDef[] = [
  { id: "actual", titleKey: "actualTitle", sourceKey: "actualSource", tagColor: "#2f6bd4" },
  { id: "plan", titleKey: "planTitle", sourceKey: "planSource", tagColor: "#16a34a" },
  { id: "expect", titleKey: "expectTitle", sourceKey: "expectSource", tagColor: "#7c5cff" },
];

/** 検索結果グラフの表示指標切替タブ（金額／単価／数量） */
export interface ResultMetricDef {
  key: ResultMetric;
  labelKey: string;
}
export const RESULT_METRIC_DEFS: ResultMetricDef[] = [
  { key: "amount", labelKey: "amount" },
  { key: "unitPrice", labelKey: "unitPrice" },
  { key: "quantity", labelKey: "quantity" },
];

/** AI分析：要因別ストレス（モック値・変動率%） */
export interface FactorDef {
  labelKey: string;
  v: number;
}
export const FACTOR_DEFS: FactorDef[] = [
  { labelKey: "quantityChangeOverTime", v: 4.2 },
  { labelKey: "quantityFluctuation", v: 9.5 },
  { labelKey: "priceChangeOverTime", v: -2.1 },
  { labelKey: "customerProductLifecycle", v: 6.8 },
  { labelKey: "exchangeRateFluctuationToPrice", v: 12.4 },
  { labelKey: "exchangeRateFluctuationToCost", v: 15.1 },
  { labelKey: "materialCostIncrease", v: 21.3 },
  { labelKey: "laborCostIncrease", v: 8.7 },
  { labelKey: "competitorStatusToQuantity", v: 11.0 },
  { labelKey: "competitorStatusToPrice", v: -5.4 },
];

export const MONTH_NUMS = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];

/** ラジオ・評価値・フィルタ値等の入力状態 */
export interface SalesDataLinkageInputState {
  subView: SalesDataLinkageSubView;
  startYm: string;
  endYm: string;
  displayCurrency: CurrencyCode;
  resultMetric: ResultMetric;
  filterValues: Record<string, string>;
  searched: Partial<Record<AxisKey, boolean>>;
  axisEvals: Partial<Record<AxisKey, RatingValue>>;
  axisNotes: Partial<Record<AxisKey, string>>;
  axisInclude: Record<AxisKey, boolean>;
  advRadios: Record<string, string>;
  evals: Partial<Record<AdvantageKey, RatingValue>>;
  advNotes: Partial<Record<AdvantageKey, string>>;
  overallRating: RatingValue | null;
  overallNote: string;
  confidence: number;
  aiRun: boolean;
  /** AI試算結果（要因別ストレス）。未実行はnull */
  aiFactors: FactorDef[] | null;
  /** AIによる全体所見（100文字程度の日本語）。未実行は空文字 */
  aiOverallNote: string;
  /** AI分析API呼び出しの状態 */
  analysisStatus: "idle" | "running" | "complete" | "error";
  /** AI分析失敗時のエラーメッセージ（未発生時はnull） */
  aiErrorMessage: string | null;
  reflectConfirm: boolean;
  /** サーバーに保存済みの営業データ連携AI分析結果のupdate_date（未保存はnull） */
  updateDate?: string | null;
  /** サーバーからの初回復元読み込みが完了したか（多重フェッチ防止用） */
  hasLoadedFromServer?: boolean;
}

export const INITIAL_STATE: SalesDataLinkageInputState = {
  subView: "industry",
  startYm: "2026-04",
  endYm: "2027-03",
  displayCurrency: "JPY",
  resultMetric: "amount",
  filterValues: {},
  searched: {},
  axisEvals: {},
  axisNotes: {},
  axisInclude: { industry: true, product: true, customer: true },
  advRadios: {},
  evals: {},
  advNotes: {},
  overallRating: null,
  overallNote: "",
  confidence: 70,
  aiRun: false,
  aiFactors: null,
  aiOverallNote: "",
  analysisStatus: "idle",
  aiErrorMessage: null,
  reflectConfirm: false,
  updateDate: null,
  hasLoadedFromServer: false,
};
